// src/lib/upload-policy.ts
//
// Espelho da política de upload do back (content/upload-policy): tamanho máximo e
// tipos por finalidade, com as MESMAS mensagens. O front checa antes de pedir a
// URL (o admin vê o motivo na hora, sem subir nada); o back continua sendo quem
// decide — e assina tipo e tamanho na URL, então o storage recusa o resto.
// Substitui as três cópias locais da regra de imagem (vitrine, banner, cards).
export type UploadPurpose =
  | "video"
  | "audio"
  | "thumbnail"
  | "material"
  | "branding"
  | "community"
  | "advertising";

const MB = 1024 * 1024;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

// Para o `accept` dos campos de imagem: o seletor de arquivo já filtra.
export const IMAGE_ACCEPT = IMAGE_TYPES.join(",");

type Policy = { maxBytes: number; label: string; accepts: (ct: string) => boolean; hint: string };

const POLICY: Record<UploadPurpose, Policy> = {
  advertising: {
    maxBytes: 50 * MB,
    label: "O arquivo",
    accepts: (ct) =>
      IMAGE_TYPES.includes(ct) || ["video/mp4", "video/webm", "video/quicktime"].includes(ct),
    hint: "Use PNG, JPG, WebP, GIF ou vídeo MP4, WebM ou MOV.",
  },
  community: {
    maxBytes: 50 * MB,
    label: "O arquivo",
    accepts: (ct) =>
      IMAGE_TYPES.includes(ct) ||
      /^(?:audio\/(?:webm|ogg|mpeg|mp4|wav|x-wav)|video\/(?:mp4|webm|quicktime))(?:;codecs=[a-zA-Z0-9, .-]+)?$/.test(
        ct,
      ) ||
      [
        "application/pdf",
        "text/plain",
        "application/msword",
        "application/vnd.ms-excel",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ].includes(ct),
    hint: "Use imagem (PNG, JPG, WebP ou GIF), áudio, vídeo, PDF, texto ou documento do Office.",
  },
  branding: {
    maxBytes: 5 * MB,
    label: "A imagem",
    accepts: (ct) => IMAGE_TYPES.includes(ct),
    hint: "Use PNG, JPG, WebP ou GIF.",
  },
  thumbnail: {
    maxBytes: 5 * MB,
    label: "A imagem",
    accepts: (ct) => IMAGE_TYPES.includes(ct),
    hint: "Use PNG, JPG, WebP ou GIF.",
  },
  video: { maxBytes: 5 * 1024 * MB, label: "O vídeo", accepts: (ct) => ct.startsWith("video/"), hint: "Envie um arquivo de vídeo." },
  audio: { maxBytes: 500 * MB, label: "O áudio", accepts: (ct) => ct.startsWith("audio/"), hint: "Envie um arquivo de áudio." },
  material: {
    maxBytes: 50 * MB,
    label: "O material",
    accepts: (ct) =>
      IMAGE_TYPES.includes(ct) ||
      ct.startsWith("audio/") ||
      ct.startsWith("video/") ||
      ct === "application/pdf" ||
      ct.startsWith("application/vnd.") ||
      ct.startsWith("application/msword") ||
      ct === "text/plain",
    hint: "Use imagem, PDF, áudio, vídeo ou documento do Office.",
  },
};

export function formatBytes(bytes: number): string {
  const gb = bytes / (1024 * MB);
  if (gb >= 1) return `${gb.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} GB`;
  return `${(bytes / MB).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

// Lança Error com a mensagem para o admin. Sem tipo (extensão desconhecida) não
// dá para assinar a URL: cai em "formato não aceito".
export function checkUpload(file: File, purpose: UploadPurpose): void {
  const policy = POLICY[purpose];
  if (!policy.accepts(file.type.toLowerCase())) throw new Error(`Formato não aceito. ${policy.hint}`);
  if (file.size > policy.maxBytes) {
    throw new Error(
      `${policy.label} tem ${formatBytes(file.size)}; o máximo é ${formatBytes(policy.maxBytes)}. Reduza e tente de novo.`,
    );
  }
}
